import React from 'react'
import { QueryClientProvider } from '@tanstack/react-query'
import { UnheadProvider, createHead } from '@unhead/react/client'
import { Provider } from 'react-redux'
import { Navigate, Route, Routes } from 'react-router'
import { PersistGate } from 'redux-persist/integration/react'
import { CanonicalPlugin, InferSeoMetaPlugin } from 'unhead/plugins'
import { BetLimitPage } from '@mobi/account/Areas/BetLimit/BetLimitPage'
import { BlockDebitCardPage } from '@mobi/account/Areas/BlockDebitCard/BlockDebitCardPage'
import {
  CONTACT_DETAILS_ROOT_ROUTE,
  routes as contactDetailsRoutes,
} from '@mobi/account/Areas/ContactDetails/Utils'
import { WITHDRAW_ROOT_ROUTE } from '@mobi/account/Areas/Withdraw/Utils'
import { routes as accountRoutes } from '@mobi/account/Utils'
import { Toasts } from '@mobi/component-library/Feedback/Toast'
import { TrustedDeviceTray } from '@core/Areas/Login/Components/MFALogin/TrustedDeviceTray'
import { VenueMode } from '@core/Areas/VenueMode/VenueMode'
import { CameraPermissionModal } from '@core/Components/CameraPermission'
import { Overlay } from '@core/Components/Overlay/Overlay'
import { Popup as PopupLegacy } from '@core/Components/Popup/Popup'
import { RequireAuth } from '@core/Components/RequireAuth'
import { HostContextMobile } from '@core/Contexts/HostContextMobile'
import { queryClient } from '@core/Data/ReactQuery'
import { useAndroidBackButton } from '@core/NativeServices/Navigation/useAndroidBackButton'
import { persistor, store } from '@core/Store'
import { AppRoutes } from './AppRoutes'
import {
  AccountBasePage,
  ActivityPage,
  BonusesPage,
  LogoutPage,
  MyBetsPage,
  MyPreferencesPage,
  PersonalDetailsPage,
  StatementsPage,
  TakeABreakPage,
  WithdrawPage,
} from './Areas/Account'
import { AppHeader } from './Areas/AppHeader/AppHeader'
import { BankAccountEdit } from './Areas/BankAccount'
import { BetLoopRoot } from './Areas/BetLoop'
import {
  BetLoopFeedPage,
  BetLoopMyProfilePage,
  BetLoopProfilePage,
  BetLoopSearchProfilesPage,
} from './Areas/BetLoop/Pages'
import { MyProfileFollowListPage } from './Areas/BetLoop/Pages/MyProfileFollowList'
import { ProfileFollowListPage } from './Areas/BetLoop/Pages/ProfileFollowList'
import { BetSlip } from './Areas/BetSlip'
import { BiometricsPage } from './Areas/Biometrics/BiometricsPage'
import { Blackbook } from './Areas/Blackbook'
import { ChangePassword, ChangeTemporaryPassword } from './Areas/ChangePassword'
import { CloseAccount } from './Areas/CloseAccount'
import { ContactDetails, ContactDetailsEditMobiWrapper } from './Areas/ContactDetails'
import { ContactUsPage } from './Areas/ContactUs/ContactUsPage'
import { Deposit } from './Areas/Deposit'
import { DepositLimits } from './Areas/DepositLimits'
import { FavouriteNumbersPage } from './Areas/FavouriteNumbers/FavouriteNumbersPage'
import { Home } from './Areas/Home/Home'
import { Jackpots } from './Areas/Jackpots'
import { KambiBetSlip, KambiClient, KambiRouteEffects, useKambiClient } from './Areas/KambiClient'
import { SportOfferingRoot } from './Areas/KambiSports/Offering/Root'
import { Login } from './Areas/Login'
import { MeetingsPage } from './Areas/Meetings'
import { MysteryBetPage } from './Areas/MysteryBet'
import { FloatingTabRadioButton } from './Areas/Navigation/Components/FloatingTabRadioButton'
import { TabNavigation } from './Areas/Navigation/Components/TabNavigation'
import { NextEventsPage } from './Areas/NextEvents/NextEventsPage'
import { QualtricsBootstrap } from './Areas/Qualtrics/QualtricsBootstrap'
import { useQualtricsInterceptor } from './Areas/Qualtrics/qualtrics'
import { RaceCard } from './Areas/RaceCard'
import { RaceCardFixedOnly } from './Areas/RaceCardFixedOnly'
import { RaceCardSky } from './Areas/RaceCardSky'
import { RaceListPage } from './Areas/RaceList/RaceList'
import { RaceEvents } from './Areas/Racing/Components/RaceEvents/RaceEvents'
import { GoodThingsPage } from './Areas/Racing/Components/RacingForm/GoodThingsPage'
import { ReverificationPopupMobi } from './Areas/Reverification/ReverificationPopup'
import { SearchPage } from './Areas/Search'
import { Settings } from './Areas/Settings'
import { MFASettingsPages } from './Areas/Settings/Components/MFASettings/Pages'
import { ShareBetOutgoing } from './Areas/ShareBet/ShareBetOutgoing'
import { ShortenedUrlHandler } from './Areas/ShortenedUrls'
import { SignUpPage } from './Areas/SignUp'
import { SkyVideoPlayer } from './Areas/SkyVideoPlayer'
import { SpecialsPage } from './Areas/Specials/SpecialsPage'
import { StructuredSearchPage } from './Areas/StructuredSearch'
import { TABLocatorWeb } from './Areas/TABLocatorWeb'
import { CurrentPoolList, Games, ResultedPoolList, TippingPage } from './Areas/Tipping'
import { DevTools, Toolbox } from './Areas/Toolbox'
import { ContentErrorBoundary } from './Components/ErrorBoundary/ContentErrorBoundary'
import { ModalContainer } from './Components/Modal'
import { useNativeAppLaunchState } from './NativeServices/AppLaunchState'
import { ForceNativeAppUpdate } from './NativeServices/ForceAppUpdate'
import { useInitAnalyticsDataLayer } from './Services/Analytics/hooks/useInitAnalyticsDataLayer'
import { VerificationAccountGate } from './Services/Gates/VerificationAccountGate'
import { GlobalCss } from './Settings/Components/GlobalCss'
import { useNavigationObserver } from './State/Navigation/hooks/useNavigationObserver'
import { AppRouter } from './Utils/routing/AppRouter'
import { appHistory } from './Utils/routing/history'

const head = createHead({
  plugins: [
    InferSeoMetaPlugin({
      twitterCard: false,
    }),
    CanonicalPlugin({
      canonicalHost: 'https://www.tabtouch.mobi',
      queryWhitelist: [],
      trailingSlash: false,
    }),
  ],
})

const App = () => (
  <Provider store={store}>
    <PersistGate persistor={persistor} loading={null}>
      <QueryClientProvider client={queryClient}>
        <UnheadProvider head={head}>
          <AppRouter history={appHistory}>
            <HostContextMobile>
              <VerificationAccountGate>
                <AppWithRoutes />
              </VerificationAccountGate>
            </HostContextMobile>
          </AppRouter>
        </UnheadProvider>
      </QueryClientProvider>
    </PersistGate>
  </Provider>
)

export const AppWithRoutes = () => {
  useNavigationObserver()
  useAndroidBackButton()
  useInitAnalyticsDataLayer()
  useNativeAppLaunchState()
  useQualtricsInterceptor()
  useKambiClient()

  return (
    <>
      <AppHeader />

      <Login />
      <Overlay />
      <Toasts />

      <TrustedDeviceTray />
      <ForceNativeAppUpdate />

      <ModalContainer />

      <ShareBetOutgoing />

      <FloatingTabRadioButton />
      <DevTools />

      <ReverificationPopupMobi />
      <PopupLegacy />
      <CameraPermissionModal />

      <GlobalCss />

      <SkyVideoPlayer />

      <BetSlip />

      <div id='js-react-app-routes' className='mobi__z-stack-content'>
        <ContentErrorBoundary>
          <Routes>
            <Route path={AppRoutes.Home} element={<Home />} />

            <Route path={AppRoutes.SignUp + '/*'} element={<SignUpPage />} />

            <Route path={AppRoutes.RaceCard} element={<RaceCard />} />

            <Route path={AppRoutes.RaceCardSky} element={<RaceCardSky />} />
            <Route path={AppRoutes.Jackpots} element={<Jackpots />} />

            <Route path={AppRoutes.NextEvents} element={<NextEventsPage />} />

            <Route path={AppRoutes.SportsRoot + '/*'} element={<KambiRouteEffects />} />

            <Route path={AppRoutes.Sports + '/*'} element={<SportOfferingRoot />} />

            <Route path={AppRoutes.Meetings} element={<MeetingsPage />} />

            <Route
              path={AppRoutes.Account}
              element={
                <RequireAuth>
                  <AccountBasePage />
                </RequireAuth>
              }
            />

            <Route
              path={AppRoutes.MyBets}
              element={
                <RequireAuth>
                  <MyBetsPage />
                </RequireAuth>
              }
            />

            <Route
              path={AppRoutes.ChangePassword}
              element={
                <RequireAuth>
                  <ChangePassword />
                </RequireAuth>
              }
            />

            <Route path={AppRoutes.ChangeTemporaryPassword} element={<ChangeTemporaryPassword />} />

            <Route path={AppRoutes.MarketingCommunications}>
              <Route
                index
                element={
                  <RequireAuth>
                    <MyPreferencesPage />
                  </RequireAuth>
                }
              />

              <Route path='optin' element={<MyPreferencesPage shouldAutoOptIn />} />
            </Route>

            <Route path={AppRoutes.CloseAccount} element={<CloseAccount />} />

            <Route path={AppRoutes.FavouriteNumbers} element={<FavouriteNumbersPage />} />

            <Route path={AppRoutes.Biometrics} element={<BiometricsPage />} />

            <Route
              path={AppRoutes.AccountActivity}
              element={
                <RequireAuth>
                  <ActivityPage />
                </RequireAuth>
              }
            />

            <Route path={AppRoutes.Search} element={<SearchPage />} />
            <Route path={AppRoutes.StructuredSearch} element={<StructuredSearchPage />} />

            <Route
              path={contactDetailsRoutes.editAddress}
              element={
                <RequireAuth>
                  <ContactDetailsEditMobiWrapper />
                </RequireAuth>
              }
            />

            <Route
              path={CONTACT_DETAILS_ROOT_ROUTE + '/*'}
              element={
                <RequireAuth>
                  <ContactDetails />
                </RequireAuth>
              }
            />

            <Route
              path={accountRoutes.personalDetails}
              element={
                <RequireAuth>
                  <PersonalDetailsPage />
                </RequireAuth>
              }
            />

            <Route
              path={WITHDRAW_ROOT_ROUTE + '/*'}
              element={
                <RequireAuth>
                  <WithdrawPage />
                </RequireAuth>
              }
            />

            <Route path={AppRoutes.ContactUs} element={<ContactUsPage />} />

            <Route
              path={AppRoutes.BankAccountEdit + '/*'}
              element={
                <RequireAuth>
                  <BankAccountEdit />
                </RequireAuth>
              }
            />

            <Route
              path={AppRoutes.Precommitment}
              element={
                <RequireAuth>
                  <BetLimitPage />
                </RequireAuth>
              }
            />

            <Route
              path={AppRoutes.TakeABreak}
              element={
                <RequireAuth>
                  <TakeABreakPage />
                </RequireAuth>
              }
            />

            <Route path={AppRoutes.MysteryBet} element={<MysteryBetPage />} />

            <Route path={AppRoutes.GoodThings} element={<GoodThingsPage />} />

            <Route
              path={AppRoutes.Specials + '/*'}
              element={
                <RequireAuth>
                  <SpecialsPage />
                </RequireAuth>
              }
            />

            <Route
              path={AppRoutes.Statements}
              element={
                <RequireAuth>
                  <StatementsPage />
                </RequireAuth>
              }
            />

            <Route path={AppRoutes.RaceList} element={<RaceListPage />} />
            <Route path={AppRoutes.RaceListForMeeting} element={<RaceListPage />} />

            <Route path={AppRoutes.RaceEvents} element={<RaceEvents />} />

            <Route path={AppRoutes.RaceCardFixedOnly} element={<RaceCardFixedOnly />} />

            <Route path={AppRoutes.Tipping} element={<TippingPage />}>
              <Route index element={<CurrentPoolList />} />
              <Route path=':sportIconCode/:poolKey' element={<Games />} />
              <Route path='results/:sportIconCode/:poolKey' element={<Games />} />
              <Route path='results' element={<ResultedPoolList />} />
              <Route path='*' element={<Navigate to={AppRoutes.Tipping} />} />
            </Route>

            <Route
              path={AppRoutes.Blackbook}
              element={
                <RequireAuth>
                  <Blackbook />
                </RequireAuth>
              }
            />

            <Route
              path={AppRoutes.BonusOffers}
              element={
                <RequireAuth>
                  <BonusesPage />
                </RequireAuth>
              }
            />

            <Route path={AppRoutes.VenueMode} element={<VenueMode />} />

            <Route path={AppRoutes.TABLocatorWeb} element={<TABLocatorWeb />} />

            <Route path={AppRoutes.Toolbox} element={<Toolbox />} />

            <Route path={AppRoutes.AccountLogout} element={<LogoutPage />} />

            <Route
              path={AppRoutes.Settings}
              element={
                <RequireAuth>
                  <Settings />
                </RequireAuth>
              }
            />

            <Route
              path={AppRoutes.SettingsMFA + '/*'}
              element={
                <RequireAuth>
                  <MFASettingsPages />
                </RequireAuth>
              }
            />

            <Route
              path={AppRoutes.Deposit + '/*'}
              element={
                <RequireAuth>
                  <Deposit flow='account-deposit' />
                </RequireAuth>
              }
            />

            <Route
              path={AppRoutes.DepositLimits}
              element={
                <RequireAuth>
                  <DepositLimits />
                </RequireAuth>
              }
            />

            <Route path={AppRoutes.BetLoop} element={<BetLoopRoot />}>
              <Route index element={<BetLoopFeedPage />} />
              <Route path='my-profile' element={<BetLoopMyProfilePage />} />
              <Route path='my-profile/follow-list' element={<MyProfileFollowListPage />} />
              <Route path='profile/:profileId' element={<BetLoopProfilePage />} />
              <Route path='profile/:profileId/follow-list' element={<ProfileFollowListPage />} />
              <Route path='search' element={<BetLoopSearchProfilesPage />} />
              <Route path='*' element={<Navigate to={AppRoutes.BetLoop} />} />
            </Route>

            <Route
              path={AppRoutes.BlockDebitCard}
              element={
                <RequireAuth>
                  <BlockDebitCardPage />
                </RequireAuth>
              }
            />

            <Route path={AppRoutes.ShortenedUrls} element={<ShortenedUrlHandler />} />

            <Route path='*' element={<Navigate to='/' />} />
          </Routes>
        </ContentErrorBoundary>

        <KambiBetSlip key='kambi-betslip' />
      </div>

      <KambiClient key='kambi-client' />
      <QualtricsBootstrap />
      <TabNavigation />
    </>
  )
}

export default App
